import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { FIRM_DASHBOARD_DATA_QUERY_KEY } from 'common/constants/services/firms';
import { useStore } from 'common/store';
import type { DashboardDefinition } from 'common/types/dashboard/dashboardDefinition';
import { useSummaryDefinitions } from 'context/SummaryDefinitionsContext';
import { type SummaryDashboardType, useGenerateDashboard } from 'services/hooks/summaryDefinitions';
import { getObjectValue } from 'utilities';

interface UseDashboardGenerationParams {
  // The dashboard being edited, sent as revision context. Null on an empty dashboard, so a first
  // generation is authored from the request alone.
  currentDefinition?: DashboardDefinition | null;
  definitionId: number;
  summaryType?: SummaryDashboardType;
}

interface UseDashboardGenerationResult {
  // Resolves true when the dashboard was generated and persisted, so the caller can clear its input
  // only on success and keep the typed prompt on failure.
  generate: (prompt: string) => Promise<boolean>;
  isGenerating: boolean;
  errorMessage: string | null;
}

const GENERIC_ERROR = 'Could not generate a dashboard. Please try again.';

// request-template.ts rejects with the raw axios error (it never constructs the generated ApiError),
// so the response body lives at error.response.data.
//
// The endpoint fails in two shapes. 422 (unbuildable prompt) and 503 (provider down) carry a `detail`
// sentence written for the user. A 400 is DRF field validation, which answers {field: ["reason"]} with
// no detail at all — without that second branch the user would just see axios's "Request failed with
// status code 400" instead of the actual reason. error.message is the last resort, for transport
// failures that never reached the API.
const resolveErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const body = error.response?.data;
    if (body && typeof body === 'object') {
      const { detail } = body as Record<string, unknown>;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
      const firstListedError = Object.values(body as Record<string, unknown>)
        .flatMap(value => (Array.isArray(value) ? value : []))
        .find((entry): entry is string => typeof entry === 'string' && Boolean(entry.trim()));
      if (firstListedError) {
        return firstListedError;
      }
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return GENERIC_ERROR;
};

// Owns the generate -> persist -> refetch sequence so the prompt components stay presentational.
// The firm id comes from the store rather than the definitions context, which does not expose its
// entityId; FirmSummaryBody reads it the same way.
//
// A populated dashboard is sent along as `current_definition`, so an edit is applied to it rather
// than replacing it. Without that context an instruction like "delete gross IRR" is the entire
// brief the model receives — it has no dashboard to remove anything from and answers with a
// placeholder tile. The model is told to treat the text as either a complete specification or an
// adjustment, which is what keeps the other direction working: rewriting the prompt to say "MOIC"
// instead of "IRR" still drops the IRR tile instead of leaving both behind.
export const useDashboardGeneration = ({
  currentDefinition = null,
  definitionId,
  summaryType = 'firm',
}: UseDashboardGenerationParams): UseDashboardGenerationResult => {
  const [storeValue] = useStore();
  const currentFirm = getObjectValue(getObjectValue(storeValue).firmInfo);
  const firmId = Number(currentFirm.id);

  const { updateDefinition, refetch } = useSummaryDefinitions();
  const generateMutation = useGenerateDashboard(firmId, summaryType);
  const queryClient = useQueryClient();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Tracked separately from the mutation's isPending because the PATCH and refetch that follow are
  // part of the same user-visible operation — the input must stay busy until the tab actually swaps.
  const [isPersisting, setIsPersisting] = useState(false);

  const generate = useCallback(
    async (prompt: string): Promise<boolean> => {
      if (!firmId) {
        setErrorMessage(GENERIC_ERROR);
        return false;
      }

      setErrorMessage(null);
      setIsPersisting(true);

      try {
        const result = await generateMutation.mutateAsync({
          current_definition: currentDefinition ?? undefined,
          prompt,
        });

        await updateDefinition(definitionId, { dashboard_definition: result.dashboard_definition });
        // The definition mutations invalidate the spreadsheet data key, never this one, so without
        // this the new tiles would render against the previous response until the 5-minute
        // staleTime expired. A partial key matches every (measurementDate, fundId) variant.
        await queryClient.invalidateQueries({
          queryKey: [FIRM_DASHBOARD_DATA_QUERY_KEY, summaryType, firmId, definitionId],
        });
        await refetch();
        return true;
      } catch (error) {
        setErrorMessage(resolveErrorMessage(error));
        return false;
      } finally {
        setIsPersisting(false);
      }
    },
    [
      currentDefinition,
      definitionId,
      firmId,
      generateMutation,
      queryClient,
      refetch,
      summaryType,
      updateDefinition,
    ]
  );

  return {
    errorMessage,
    generate,
    isGenerating: generateMutation.isPending || isPersisting,
  };
};
